import React, { useEffect, useMemo } from 'react';
import _ from 'lodash';
import { useStateSelector } from '../../../../../data/global/dataSelectors';
import { DetailField, DetailFields } from '../../../../../components/basic/DetailFields';
import { useDispatcher } from '../../../../../data/global/hooks';
import { AdminApplicationActions } from '../../../../../data/global/actions/admin/AdminApplicationActions';
import {
  ApplicationInvestigationUploadedDocument,
  ApplicationStatusApplicationDocuments,
  ApplicationStatusInvestigations,
  RequestedInvestigationDocument,
} from '@tradingblock/types';
import dayjs from 'dayjs';
import { getRequestedParticipantsDocuments } from '@tradingblock/api';

export const ApplicationDocumentsRequested: React.FunctionComponent<{}> = () => {
  const application = useStateSelector(s => s.private.applications.currentItem.application);
  const investigation = useStateSelector(s => s.private.applications.currentItem.investigation.response.payload);
  const { dispatch } = useDispatcher();

  const { state, investigations, applications } = investigation || {
    state: undefined,
    investigations: [],
    applications: [],
  };

  const requestedParticipantsDocuments = useMemo(() => {
    return getRequestedParticipantsDocuments(investigations, applications);
  }, [investigations, applications, investigation]);

  const areDocumentsRequested = useMemo(() => {
    return requestedParticipantsDocuments.some(
      participant =>
        participant.requestedCatagories.investigations.length > 0 ||
        participant.requestedCatagories.application.length > 0
    );
  }, [requestedParticipantsDocuments, investigation]);

  const areDocumentsUploaded = useMemo(() => {
    return requestedParticipantsDocuments.some(participant => participant.uploadedDocuments.length > 0);
  }, [requestedParticipantsDocuments, investigation]);

  useEffect(() => {
    if (application.accountId) {
      dispatch(AdminApplicationActions.requestInvestigations({ accountId: application.accountId }));
    }
  }, []);

  const fields = useMemo(() => {
    let fieldList: DetailField[] = [];
    if (application) {
      fieldList = [
        ...fieldList,
        {
          Value: () => (
            <>
              <span className="fields-title">Documents</span>
            </>
          ),
        },
        {
          Value: () => null,
        },
      ];
      requestedParticipantsDocuments.map((participant, index) => {
        if (areDocumentsRequested && areDocumentsUploaded) {
          fieldList = [
            ...fieldList,
            {
              Value: () => (
                <span className="props-title" style={{ fontWeight: 'bold' }}>
                  {participant.firstName} {participant.middleInitial || ''} {participant.lastName}{' '}
                  {participant.suffix || ''}
                </span>
              ),
            },
            {
              Value: () => null,
            },
            {
              Value: () => (
                <>
                  <span>
                    <span className="txt-sm">Documents Requested to Verify: </span>
                    <br />
                    <br />
                    {participant.requestedCatagories.application.map((category, index) => (
                      <span key={`application_request-${category.name}` + index}>
                        <span>
                          <span>{_.startCase(category.name)}</span>
                        </span>
                        <br />
                        <span className="txt-sm" style={{ color: 'var(--salmon)' }}>
                          {category.requestedDocuments.join(', ')}
                        </span>
                        <br />
                      </span>
                    ))}
                    {participant.requestedCatagories.investigations.map((category, index) => (
                      <span key={`investigation_request-${category.name}` + index}>
                        <span>
                          <span>{_.startCase(category.name)}</span>
                        </span>
                        <br />
                        <span className="txt-sm" style={{ color: 'var(--salmon)' }}>
                          {category.requestedDocuments.join(', ')}
                        </span>
                        <br />
                      </span>
                    ))}
                  </span>
                </>
              ),
            },
            {
              Value: () => (
                <>
                  <span className="txt-sm">Documents Uploaded:</span>
                  <br />
                  <br />
                  {participant.uploadedDocuments.map((uploadedDocument, index) => (
                    <span key={`application_upload-${uploadedDocument.miscDocumentId}` + index}>
                      <span>
                        <span>{_.startCase(uploadedDocument.documentName)}</span>
                        <br />
                        <span className="mute txt-sm">Original Document Name: {uploadedDocument.originalFileName}</span>
                        <br />
                        <span className="mute txt-sm">Status: {_.startCase(uploadedDocument.state)}</span>
                        <br />
                        {uploadedDocument.takenOn && (
                          <span className="mute txt-sm">
                            Taken On: {dayjs(uploadedDocument.takenOn).format('MM/DD/YYYY')}
                          </span>
                        )}
                      </span>
                      <br />
                      <br />
                    </span>
                  ))}
                </>
              ),
            },
          ];
        }
        if (areDocumentsRequested && !areDocumentsUploaded) {
          fieldList = [
            ...fieldList,
            {
              Value: () => (
                <span className="props-title" style={{ fontWeight: 'bold' }}>
                  {participant.firstName} {participant.middleInitial || ''} {participant.lastName}{' '}
                  {participant.suffix || ''}
                </span>
              ),
            },
            {
              Value: () => null,
            },
            {
              Value: () => (
                <>
                  <span>
                    <span className="txt-sm">Documents Requested to Verify: </span>
                    <br />
                    <br />
                    {participant.requestedCatagories.application.map((category, index) => (
                      <span key={`application_request-${category.name}` + index}>
                        <span>
                          <span>{_.startCase(category.name)}</span>
                        </span>
                        <br />
                        <span className="txt-sm" style={{ color: 'var(--salmon)' }}>
                          {category.requestedDocuments.join(', ')}
                        </span>
                        <br />
                      </span>
                    ))}
                    {participant.requestedCatagories.investigations.map((category, index) => (
                      <span key={`investigation_request-${category.name}` + index}>
                        <span>
                          <span>{_.startCase(category.name)}</span>
                        </span>
                        <br />
                        <span className="txt-sm" style={{ color: 'var(--salmon)' }}>
                          {category.requestedDocuments.join(', ')}
                        </span>
                        <br />
                      </span>
                    ))}
                  </span>
                </>
              ),
            },
          ];
        }
        if (!areDocumentsRequested && areDocumentsUploaded) {
          fieldList = [
            ...fieldList,
            {
              Value: () => (
                <span className="props-title" style={{ fontWeight: 'bold' }}>
                  {participant.firstName} {participant.middleInitial || ''} {participant.lastName}{' '}
                  {participant.suffix || ''}
                </span>
              ),
            },
            {
              Value: () => null,
            },
            {
              Value: () => <span className="txt-sm">Documents Uploaded:</span>,
            },
            {
              Value: () => null,
            },
          ];
          participant.uploadedDocuments.map((uploadedDocument, index) => {
            fieldList = [
              ...fieldList,
              {
                Value: () => (
                  <div key={`application_upload-${uploadedDocument.miscDocumentId}` + index}>
                    <span>
                      <span>{_.startCase(uploadedDocument.documentName)}</span>
                      <br />
                      <span className="mute txt-sm">Original Document Name: {uploadedDocument.originalFileName}</span>
                      <br />
                      <span className="mute txt-sm">Status: {_.startCase(uploadedDocument.state)}</span>
                      <br />
                      {uploadedDocument.takenOn && (
                        <span className="mute txt-sm">
                          Taken On: {dayjs(uploadedDocument.takenOn).format('MM/DD/YYYY')}
                        </span>
                      )}
                    </span>
                    <br />
                    <br />
                  </div>
                ),
              },
            ];
          });
        }
      });
    }
    return fieldList;
  }, [application, areDocumentsRequested, requestedParticipantsDocuments]);

  return (
    <>
      {(areDocumentsRequested || areDocumentsUploaded) && (
        <div className="screen-body-section">
          <DetailFields fieldList={fields} />
        </div>
      )}

      {!areDocumentsRequested && !areDocumentsUploaded && (
        <div className="screen-body-section">
          <span className="fields-title">No Documents Requested</span>
        </div>
      )}
    </>
  );
};
